import { useCallback, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import {
  QrCode,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  Share2,
  MessageCircle,
  Mail,
} from "lucide-react";

/**
 * Accountant QR Invite — Settings drop-in.
 *
 * Flow:
 *   1. Tradie taps "Generate invite" → POST /api/accountant/invite
 *   2. UI renders a QR (svg) that encodes the returned invite_url
 *   3. Tradie shows the QR to their accountant OR shares the link via
 *      WhatsApp / Email / Native Share
 *   4. Accountant opens the link → /onboard-accountant → fills their name +
 *      email → the tradie's BusinessProfile.accountant_name/_email gets set.
 *
 * The invite URL is a JWT-signed 7-day link — copy/share is safe within that
 * window; regenerating produces a fresh token and effectively rotates the old.
 */
export default function AccountantInviteQR({ onAccepted }) {
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null); // { token, invite_url, expires_in_days }
  const [copied, setCopied] = useState(false);

  const generate = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/accountant/invite");
      setInvite(data);
      toast.success("Invite link generated");
    } catch (err) {
      devLog.warn("accountant invite failed", err);
      toast.error("Could not generate invite");
    } finally {
      setBusy(false);
    }
  }, []);

  const copyLink = useCallback(async () => {
    if (!invite?.invite_url) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(invite.invite_url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = invite.invite_url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Link copied");
    } catch (err) {
      devLog.warn("clipboard copy failed", err);
      toast.error("Copy failed — long-press the link");
    }
  }, [invite]);

  const shareVia = useCallback(async (kind) => {
    if (!invite?.invite_url) return;
    const body =
      `You're invited to be the accountant on BuildIntel AI.\n\n` +
      `Tap this link to add your details — it takes 20 seconds:\n${invite.invite_url}\n\n` +
      `(Link expires in ${invite.expires_in_days} days.)`;
    const subject = "BuildIntel AI · Be my accountant";
    try {
      if (kind === "whatsapp") {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(body)}`,
          "_blank",
          "noopener,noreferrer"
        );
      } else if (kind === "email") {
        window.location.href = `mailto:?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
      } else if (kind === "native" && navigator.share) {
        await navigator.share({ title: subject, text: body, url: invite.invite_url });
        toast.success("Invite shared");
      } else if (kind === "native") {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(body)}`,
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        devLog.warn("invite share failed", err);
        toast.error("Share failed — try Copy instead");
      }
    }
  }, [invite]);

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="accountant-qr-section">
      <div className="flex items-baseline justify-between">
        <div className="font-heading font-bold inline-flex items-center gap-2">
          <QrCode className="w-4 h-4 text-[#FF9500]" />
          Invite your accountant
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
          One-tap contact
        </div>
      </div>
      <p className="text-[11px] text-white/50 leading-snug">
        Generate a QR your accountant scans with their phone camera. Opens a short
        form where they save their <b>name + email</b>, which prefills the
        Email button on your BAS, TPAR &amp; Accountant Pack cards.
      </p>

      {!invite && (
        <button
          type="button"
          data-testid="qr-generate-btn"
          onClick={generate}
          disabled={busy}
          className="w-full h-11 rounded-xl bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <QrCode className="w-4 h-4" />
          )}
          Generate invite QR
        </button>
      )}

      {invite && (
        <div className="space-y-3" data-testid="qr-invite-block">
          <div className="mx-auto w-fit p-3 bg-white rounded-2xl" data-testid="qr-canvas">
            <QRCodeSVG
              value={invite.invite_url}
              size={192}
              bgColor="#FFFFFF"
              fgColor="#0F0F0F"
              level="M"
              includeMargin={false}
            />
          </div>

          <div className="text-[10px] text-white/40 text-center">
            Point their camera at this code — no app needed. Link expires in{" "}
            <b>{invite.expires_in_days} days</b>.
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/5 p-2">
            <input
              readOnly
              data-testid="qr-invite-link"
              value={invite.invite_url}
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[11px] text-white/70 truncate"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              data-testid="qr-copy-link"
              onClick={copyLink}
              className="shrink-0 w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
              title="Copy link"
              aria-label="Copy invite link"
            >
              {copied ? (
                <Check className="w-4 h-4 text-[#FF9500]" />
              ) : (
                <Copy className="w-4 h-4 text-white/60" />
              )}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              data-testid="qr-share-whatsapp"
              onClick={() => shareVia("whatsapp")}
              className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
              title="Share invite via WhatsApp"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span>WhatsApp</span>
            </button>
            <button
              type="button"
              data-testid="qr-share-email"
              onClick={() => shareVia("email")}
              className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
              title="Share invite via Email"
            >
              <Mail className="w-4 h-4 text-[#60A5FA]" />
              <span>Email</span>
            </button>
            <button
              type="button"
              data-testid="qr-share-native"
              onClick={() => shareVia("native")}
              className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
              title="Share invite via device"
            >
              <Share2 className="w-4 h-4 text-[#FF9500]" />
              <span>Share</span>
            </button>
          </div>

          <button
            type="button"
            data-testid="qr-regenerate"
            onClick={generate}
            disabled={busy}
            className="w-full h-9 rounded-lg text-[11px] text-white/50 hover:text-white/80 inline-flex items-center justify-center gap-1.5 transition"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Rotate to a fresh link
          </button>
        </div>
      )}
    </section>
  );
}
